import i18next from "i18next";
import { ReactElement } from "react";
import DownPaymentInput from "Shared/CurrencyInput/DownPaymentInput";
import AccessoriesCell from "src/components/MyGarage/TableCompare/Cells/AccessoriesCell";
import BankOfferCell from "src/components/MyGarage/TableCompare/Cells/BankOfferCell";
import CellStrikethrough from "src/components/MyGarage/TableCompare/Cells/CellStrikethrough";
import ConditionalRebatesCell from "src/components/MyGarage/TableCompare/Cells/ConditionalRebatesCell";
import PaymentFrequencyCell from "src/components/MyGarage/TableCompare/Cells/PaymentFrequencyCell";
import { DealerVehicles } from "src/components/MyGarage/TableCompare/index";
import { EMPTY_VALUE_DASH } from "src/constants";
import { getPaymentFrequencyText, localizeCurrencyToWholeNumber, localizeCurrencyWithDecimals } from "src/utils";
import { canShowMSRP, isUS, showStrikethroughForMSRP } from "src/utils/account";
import { getOfferData, getPaymentPrice } from "src/utils/vehicles";
import { ApiVehicle } from "Types/api/Vehicle";
import { AccountState, VehicleState } from "Types/state";
import CellDropdown from "./Cells/CellDropdown";
import CellText from "./Cells/CellText";
import TradeInValue from "./Cells/TradeInValue";
import { DISABLED_CELL } from "./shared";

export const TRANSLATION_KEY = "MyGarage.TableCompare";

export const PAYMENT_TYPE_KEY = "payments|selected";
export const PAYMENT_TYPE_PLACEHOLDER = "::paymenttype::";

export enum FORMATS {
    Currency,
    Number,
}

export interface Feature {
    id: string;
    cellType: any;
    dataFunction?: (...args: any[]) => string | [string, boolean];
    dataKey?: string;
    disable?: (...args: any[]) => string[] | string[];
    format?: FORMATS;
    isModal?: boolean;
    label: string | ((vehicles: VehicleState[], region: string) => string);
    options?: (vehicle: VehicleState) => any;
    valueFormat?: FORMATS;
    hasChanged?: (vin: string, type: "cash" | "loan" | "lease" | "dealer", offer?: string) => boolean;
}

const hasChanged = (path: string | string[], dealerChanges: string[]) => (
    vin: string,
    type: "cash" | "loan" | "lease" | "dealer",
    offer: string = "0"
) => {
    if (Array.isArray(path)) {
        return !!dealerChanges.find((value) =>
            path.some((p) =>
                RegExp(
                    `^${p
                        .replace(/\$VIN/, vin)
                        .replace(/\$TYPE/, type)
                        .replace(/\$OFFER/, offer)}`
                ).test(value)
            )
        );
    }

    return !!dealerChanges.find((value) =>
        RegExp(
            `^${path
                .replace(/\$VIN/, vin)
                .replace(/\$TYPE/, type)
                .replace(/\$OFFER/, offer)}`
        ).test(value)
    );
};

const getPurchasePriceLabelFromAccount = (account: AccountState) => {
    if (!account || !account.account_disclaimers || !account.account_disclaimers.garage_grid_purchase_price_label) {
        return i18next.t("MyGarage.TableCompare.PurchasePrice.defaultLabel");
    }
    return account.account_disclaimers.garage_grid_purchase_price_label;
};

const getMsrpFeature = (account: AccountState, dealerChanges: string[]): Feature => {
    return {
        cellType: CellStrikethrough(showStrikethroughForMSRP(account)),
        dataFunction: (vehicle: VehicleState) =>
            vehicle.msrp
                ? [
                      localizeCurrencyWithDecimals(vehicle.msrp),
                      vehicle.msrp ===
                          (vehicle.payments?.dealer?.price ?? vehicle.payments[vehicle.payments.selected]?.price),
                  ]
                : [EMPTY_VALUE_DASH, true],

        format: FORMATS.Currency,
        id: "MSRP",
        label: i18next.t(`${TRANSLATION_KEY}.msrpLabel`),
        hasChanged: hasChanged(`/vehicles/$VIN/msrp`, dealerChanges),
    };
};

const getDealerDiscountFeature = (account: AccountState): Feature => {
    return {
        cellType: CellText,
        dataFunction: (vehicle: VehicleState) => {
            const dealerDiscount =
                vehicle.payments?.dealer?.dealer_discount ??
                vehicle.payments[vehicle.payments.selected]?.dealer_discount;

            if (dealerDiscount === null || dealerDiscount === undefined || dealerDiscount === 0) {
                return EMPTY_VALUE_DASH;
            }

            return localizeCurrencyWithDecimals(Math.abs(dealerDiscount));
        },
        id: "DealerDiscount",
        label: (vehicles: VehicleState[], region: string) => {
            const negativeDiscounts = vehicles.every((vehicle) => {
                const dealerDiscount =
                    vehicle.payments?.dealer?.dealer_discount ??
                    vehicle.payments[vehicle.payments.selected]?.dealer_discount;
                return dealerDiscount !== null && dealerDiscount !== undefined && dealerDiscount < 0;
            });

            if (negativeDiscounts) {
                return account.account_disclaimers.garage_grid_dealer_addendum;
            }

            return account.account_disclaimers.garage_grid_dealer_discount;
        },
    };
};

const getFeatures = (account: AccountState, hasLeaseInGarage: boolean, dealerChanges: string[]) =>
    [
        {
            cellType: CellText,
            dataFunction: (vehicle: VehicleState) =>
                getPaymentPrice(vehicle) ? localizeCurrencyWithDecimals(getPaymentPrice(vehicle)) : EMPTY_VALUE_DASH,
            format: FORMATS.Currency,
            id: "YourPrice",
            label: account.account_disclaimers.base_price,
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/price`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: calculateIncentivesTotal,
            id: "Incentives",
            label: i18next.t(`${TRANSLATION_KEY}.incentivesLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/offers/$OFFER/rebates`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: getPurchasePrice,
            id: "PurchasePrice",
            label: getPurchasePriceLabelFromAccount(account),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/offers/$OFFER/purchase_price`, dealerChanges),
        },
        {
            cellType: CellDropdown,
            dataFunction: getPaymentTypeForVehicle,
            dataKey: PAYMENT_TYPE_KEY,
            id: "PaymentType",
            label: i18next.t(`${TRANSLATION_KEY}.paymentTypeLabel`),
            options: (vehicle: VehicleState) => {
                const options = [];

                if (vehicle.payments.lease?.enabled) {
                    options.push({ value: "lease", text: i18next.t(`${TRANSLATION_KEY}.leasePaymentTypeOption`) });
                }

                if (vehicle.payments.loan?.enabled) {
                    options.push({ value: "loan", text: i18next.t(`${TRANSLATION_KEY}.loanPaymentTypeOption`) });
                }

                if (vehicle.payments.cash?.enabled) {
                    options.push({ value: "cash", text: i18next.t(`${TRANSLATION_KEY}.cashPaymentTypeOption`) });
                }

                return {
                    cash: options,
                    lease: options,
                    loan: options,
                };
            },
            hasChanged: hasChanged(`/vehicles/$VIN/payments/selected`, dealerChanges),
        },
        {
            cellType: PaymentFrequencyCell,
            disable: ["cash"],
            id: "Frequency",
            label: i18next.t(`${TRANSLATION_KEY}.frequency`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/frequency`, dealerChanges),
        },
        {
            cellType: CellDropdown,
            dataKey: `payments|${PAYMENT_TYPE_PLACEHOLDER}|term`,
            disable: ["cash"],
            id: "Term",
            label: i18next.t(`${TRANSLATION_KEY}.termLabel`),
            options: (vehicle: VehicleState) => ({
                lease: vehicle.account.finance_settings.lease.term.options.map((term) => ({
                    value: term,
                    text: `${term} ${i18next.t(`${TRANSLATION_KEY}.months`)}`,
                })),
                loan: vehicle.account.finance_settings.loan.term.options.map((term) => ({
                    value: term,
                    text: `${term} ${i18next.t(`${TRANSLATION_KEY}.months`)}`,
                })),
            }),
            valueFormat: FORMATS.Number,
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/term`, dealerChanges),
        },
        {
            cellType: CellDropdown,
            dataKey: `payments|${PAYMENT_TYPE_PLACEHOLDER}|miles`,
            disable: ["cash", "loan"],
            id: "Mileage",
            label: (vehicles: VehicleState[], region: string) =>
                isUS(region)
                    ? i18next.t(`${TRANSLATION_KEY}.leaseMileageLabel`)
                    : i18next.t(`${TRANSLATION_KEY}.leaseKmLabel`),
            options: (vehicle: VehicleState) => ({
                lease: vehicle.account.finance_settings.lease.miles.options.map((mile) => ({
                    text: `${mile} ${
                        vehicle.account.localization.distance?.label === "miles"
                            ? i18next.t("MyGarage.TableCompare.miles")
                            : i18next.t("MyGarage.TableCompare.kilometers")
                    }`,
                    value: mile,
                })),
            }),
            valueFormat: FORMATS.Number,
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/miles`, dealerChanges),
        },
        {
            cellType: DownPaymentInput,
            dataKey: `payments|${PAYMENT_TYPE_PLACEHOLDER}|down_payment`,
            disable: ["cash"],
            format: FORMATS.Currency,
            id: "DownPayment",
            label:
                (hasLeaseInGarage
                    ? account.account_disclaimers?.down_payment_label_lease
                    : account.account_disclaimers?.down_payment_label_loan) ??
                i18next.t(`${TRANSLATION_KEY}.downPaymentLabel`),
            valueFormat: FORMATS.Currency,
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/down_payment`, dealerChanges),
        },
        {
            cellType: TradeInValue,
            id: "TradeInValue",
            label: i18next.t(`${TRANSLATION_KEY}.tradeInValue`),
            valueFormat: FORMATS.Currency,
            hasChanged: hasChanged(`/tradein`, dealerChanges),
        },
        {
            cellType: BankOfferCell,
            dataKey: `payments|${PAYMENT_TYPE_PLACEHOLDER}|selected_offer`,
            id: "BestBankOffer",
            label: i18next.t(`${TRANSLATION_KEY}.bestBankOfferLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/$TYPE/selected_offer`, dealerChanges),
        },
    ] as Feature[];

const displayNonZeroCurrencyWithDecimals = (value: any): string => {
    return value ? localizeCurrencyWithDecimals(value) : EMPTY_VALUE_DASH;
};

const getDealerOfferFeatures = (
    account: AccountState,
    dealerVehicles: DealerVehicles,
    hasLeaseInGarage: boolean,
    dealerChanges: string[]
) => {
    return [
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) => displayNonZeroCurrencyWithDecimals(dealerVehicles[v.vin]?.price),
            id: "YourPrice",
            label: account.account_disclaimers.base_price,
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/price`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) =>
                displayNonZeroCurrencyWithDecimals(dealerVehicles[v.vin]?.incentivesTotal),
            id: "Incentives",
            label: i18next.t(`${TRANSLATION_KEY}.incentivesLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/rebates`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) => displayNonZeroCurrencyWithDecimals(dealerVehicles[v.vin]?.purchasePrice),
            id: "PurchasePrice",
            label: getPurchasePriceLabelFromAccount(account),
            hasChanged: hasChanged(
                [`/vehicles/$VIN/payments/dealer/price`, `/vehicles/$VIN/payments/dealer/rebates`],
                dealerChanges
            ),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) => {
                switch (dealerVehicles[v.vin]?.paymentType) {
                    case "cash":
                        return i18next.t(`${TRANSLATION_KEY}.cashPaymentTypeOption`);
                    case "loan":
                        return i18next.t(`${TRANSLATION_KEY}.loanPaymentTypeOption`);
                    case "lease":
                        return i18next.t(`${TRANSLATION_KEY}.leasePaymentTypeOption`);
                    default:
                        return EMPTY_VALUE_DASH;
                }
            },
            id: "PaymentType",
            label: i18next.t(`${TRANSLATION_KEY}.paymentTypeLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/type`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) =>
                dealerVehicles[v.vin]?.paymentType === "cash"
                    ? EMPTY_VALUE_DASH
                    : {
                          weekly: i18next.t("CustomizePayment.PaymentFrequency.weekly"),
                          biweekly: i18next.t("CustomizePayment.PaymentFrequency.biweekly"),
                          monthly: i18next.t("CustomizePayment.PaymentFrequency.monthly"),
                      }[dealerVehicles[v.vin]?.frequency],
            id: "Frequency",
            label: i18next.t(`${TRANSLATION_KEY}.frequency`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/frequency`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) => {
                return dealerVehicles[v.vin]?.term
                    ? `${dealerVehicles[v.vin]?.term} ${i18next.t(`${TRANSLATION_KEY}.months`)}`
                    : EMPTY_VALUE_DASH;
            },
            id: "Term",
            label: i18next.t(`${TRANSLATION_KEY}.termLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/term`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) =>
                dealerVehicles[v.vin]?.leaseMileage
                    ? `${dealerVehicles[v.vin]?.leaseMileage} ${
                          v.account.localization.distance?.label === "miles"
                              ? i18next.t("MyGarage.TableCompare.miles")
                              : i18next.t("MyGarage.TableCompare.kilometers")
                      }`
                    : EMPTY_VALUE_DASH,
            id: "Mileage",
            label: (vehicles: VehicleState[], region: string) =>
                isUS(region)
                    ? i18next.t(`${TRANSLATION_KEY}.leaseMileageLabel`)
                    : i18next.t(`${TRANSLATION_KEY}.leaseKmLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/miles`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) =>
                dealerVehicles[v.vin]?.paymentType === "cash"
                    ? EMPTY_VALUE_DASH
                    : localizeCurrencyToWholeNumber(dealerVehicles[v.vin]?.downPayment),
            id: "DownPayment",
            label:
                (hasLeaseInGarage
                    ? account.account_disclaimers?.down_payment_label_lease
                    : account.account_disclaimers?.down_payment_label_loan) ??
                i18next.t(`${TRANSLATION_KEY}.downPaymentLabel`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/down_payment`, dealerChanges),
        },
        {
            cellType: CellText,
            dataFunction: (v: VehicleState) => localizeCurrencyToWholeNumber(dealerVehicles[v.vin]?.tradeInEstimate),
            id: "TradeInValue",
            label: i18next.t(`${TRANSLATION_KEY}.tradeInValue`),
            hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/tradein`, dealerChanges),
        },
        {
            cellType: BankOfferCell,
            id: "BestBankOffer",
            label: i18next.t(`${TRANSLATION_KEY}.bestBankOfferLabel`),
            hasChanged: hasChanged(
                [`/vehicles/$VIN/payments/dealer/interest_rate`, `/vehicles/$VIN/payments/dealer/lender`],
                dealerChanges
            ),
        },
    ] as Feature[];
};

const getAccessoriesFeature = (account: AccountState, dealerChanges: string[]) => ({
    cellType: AccessoriesCell,
    id: "Accessories",
    label: account?.account_disclaimers?.upgrades_label,
    hasChanged: hasChanged(`/vehicles/$VIN/accessories`, dealerChanges),
});

const getDealerOfferAccessoriesFeature = (
    dealerVehicles: DealerVehicles,
    account: AccountState,
    dealerChanges: string[]
) => ({
    cellType: CellText,
    dataFunction: (v: VehicleState) => localizeCurrencyWithDecimals(dealerVehicles[v.vin]?.accessoriesTotal),
    id: "Accessories",
    label: account?.account_disclaimers?.upgrades_label,
    hasChanged: hasChanged(`/vehicles/$VIN/payments/dealer/accessories`, dealerChanges),
});

const getConditionalRebatesFeature = (dealerChanges: string[]) => ({
    cellType: ConditionalRebatesCell,
    id: "ConditionalRebates",
    label: i18next.t(`${TRANSLATION_KEY}.conditionalRebatesLabel`),
    hasChanged: hasChanged(`/rebates`, dealerChanges),
});

const getDealerOfferConditionalRebatesFeature = (dealerVehicles: DealerVehicles) => ({
    cellType: CellText,
    dataFunction: (v: VehicleState) => EMPTY_VALUE_DASH,
    id: "ConditionalRebates",
    label: i18next.t(`${TRANSLATION_KEY}.conditionalRebatesLabel`),
});

export function getFeatureList(
    account: AccountState,
    hasAccessories: boolean,
    hasLeaseInGarage: boolean,
    dealerChanges: string[]
) {
    let baseFeatures = getFeatures(account, hasLeaseInGarage, dealerChanges);

    if (account.integration_settings?.Financing?.conditional_rebates !== "none") {
        baseFeatures.splice(2, 0, getConditionalRebatesFeature(dealerChanges));
    }

    if (account?.integration_settings?.Financing?.ui?.dealer_discount_row) {
        baseFeatures = [
            {
                ...getDealerDiscountFeature(account),
                hasChanged: hasChanged([`/vehicles/$VIN/msrp`, `/vehicles/$VIN/payments/$TYPE/price`], dealerChanges),
            },
            ...baseFeatures,
        ];
    }

    if (canShowMSRP(account)) {
        baseFeatures = [getMsrpFeature(account, dealerChanges), ...baseFeatures];
    }

    if (!hasAccessories) {
        return baseFeatures;
    }

    return [...baseFeatures, getAccessoriesFeature(account, dealerChanges)];
}

export function getDealerOfferFeatureList(
    account: AccountState,
    hasAccessories: boolean,
    dealerVehicles: DealerVehicles,
    hasLeaseInGarage: boolean,
    dealerChanges: string[]
) {
    let baseFeatures = getDealerOfferFeatures(account, dealerVehicles, hasLeaseInGarage, dealerChanges);

    if (account.integration_settings?.Financing?.conditional_rebates !== "none") {
        baseFeatures.splice(2, 0, getDealerOfferConditionalRebatesFeature(dealerVehicles));
    }

    if (account?.integration_settings?.Financing?.ui?.dealer_discount_row) {
        baseFeatures = [
            {
                ...getDealerDiscountFeature(account),
                hasChanged: hasChanged([`/vehicles/$VIN/msrp`, `/vehicles/$VIN/payments/dealer/price`], dealerChanges),
            },
            ...baseFeatures,
        ];
    }

    if (canShowMSRP(account)) {
        baseFeatures = [getMsrpFeature(account, dealerChanges), ...baseFeatures];
    }

    if (!hasAccessories) {
        return baseFeatures;
    }

    return [...baseFeatures, getDealerOfferAccessoriesFeature(dealerVehicles, account, dealerChanges)];
}

export function getNestedProperty(data: any, key: string): any {
    if (!data) {
        return "";
    }
    const properties = key.split("|");

    if (properties.length === 1) {
        return data[properties[0]];
    }

    return getNestedProperty(data[properties[0]], properties.splice(1, properties.length - 1).join("|"));
}

export function performTemplateReplacementOnKey(data: any, key: string): string {
    if (!key) {
        return "";
    }

    if (!key.includes(PAYMENT_TYPE_PLACEHOLDER)) {
        return key;
    }

    const replacement = getPaymentType(data);
    return key.replace(PAYMENT_TYPE_PLACEHOLDER, replacement);
}

export function formatValue(value: any, format: any): string {
    if (value === undefined || value === null) {
        return "";
    }
    switch (format) {
        case FORMATS.Currency:
            return localizeCurrencyToWholeNumber(value);
        default:
            return value.toString();
    }
}

function formatOutputValue(value: any, valueFormat?: FORMATS.Number | FORMATS.Currency) {
    let val = value;
    switch (valueFormat) {
        case FORMATS.Number:
            val = parseInt(value, 10);
            break;
        case FORMATS.Currency:
            val = parseFloat(value.replace("$", "").replace(",", ""));
            break;
    }
    return val;
}

export function pipeToObject(list: string, value: any, valueFormat?: FORMATS.Number | FORMATS.Currency) {
    const items = list.split("|").reverse();
    const obj = items.reduce<any>((memo, item, index) => {
        const newMemo = {};
        if (index === 0) {
            newMemo[item] = formatOutputValue(value, valueFormat);
        } else {
            newMemo[item] = memo;
        }
        return newMemo;
    }, {});
    return obj;
}

export function getActualVehicleCount(list: Array<{ loading?: boolean; isDeleting?: boolean }>) {
    return list.reduce((acc, cur) => {
        if (!cur.loading && !cur.isDeleting) {
            acc++;
        }

        return acc;
    }, 0);
}

export function getPaymentTypeForVehicle(vehicleDeal: ApiVehicle) {
    const nestedType = getNestedProperty(vehicleDeal, PAYMENT_TYPE_KEY);
    if (nestedType) {
        return nestedType;
    }

    return "loan";
}

export function getPaymentType(vehicleDeal: ApiVehicle) {
    const nestedType = getNestedProperty(vehicleDeal, PAYMENT_TYPE_KEY);
    if (nestedType) {
        return nestedType;
    }

    return "loan";
}

export function getCurrentOffers(vehicleDeal: ApiVehicle, paymentType: string) {
    if (!vehicleDeal) {
        return [];
    }
    const payment = vehicleDeal.payments && vehicleDeal.payments[paymentType];
    return (payment && payment.offers) || [];
}

export function getCurrentOffer(vehicleDeal: ApiVehicle, offerIndex?: number) {
    if (!vehicleDeal) {
        return {};
    }
    const paymentType = getPaymentType(vehicleDeal);

    if (!paymentType || !vehicleDeal.payments) {
        return {};
    }

    const payment = vehicleDeal.payments[paymentType];
    const selectedOffer = offerIndex !== undefined ? offerIndex : payment.selected_offer || 0;
    return payment.offers ? payment.offers[selectedOffer] || {} : {};
}

export function getCurrentMonthlyPayment(vehicleDeal: ApiVehicle) {
    const offer = getCurrentOffer(vehicleDeal);
    return offer.payment;
}

export function getCurrentMonthlyPaymentFormatted(vehicleDeal: ApiVehicle) {
    const offer = getCurrentOffer(vehicleDeal);
    return `${localizeCurrencyWithDecimals(offer.payment || 0)} ${getPaymentFrequencyText(offer.frequency)}`;
}

function calculateIncentivesTotal(vehicleDeal: ApiVehicle) {
    const currentOffer = getCurrentOffer(vehicleDeal);
    const rebates = currentOffer.rebates || [];

    const rebateTotal = rebates.reduce((total: number, rebate: any) => {
        if (rebate.value) {
            total = total + rebate.value;
        }
        return total;
    }, 0);
    if (rebateTotal === 0) {
        return DISABLED_CELL;
    }
    return localizeCurrencyWithDecimals(rebateTotal);
}

export const getPurchasePrice = (vehicle: VehicleState): string | ReactElement<any> => {
    const offer = getOfferData(vehicle);
    if (!offer || !offer.purchase_price) return DISABLED_CELL;
    return localizeCurrencyWithDecimals(offer.purchase_price);
};

export function determineVehiclePaddingSlot(vehiclesLength: number): number {
    const minVehicles = 4;
    const maxVehicles = 6;
    const additionalVehicles =
        vehiclesLength < minVehicles
            ? minVehicles - vehiclesLength
            : Math.min(vehiclesLength + 1, maxVehicles) - vehiclesLength;
    return additionalVehicles;
}
